import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/axios';
import toast from 'react-hot-toast';
import {
    FiUser,
    FiMail,
    FiPhone,
    FiMapPin,
    FiEdit2,
    FiSave,
    FiX,
    FiBriefcase,
    FiGlobe,
    FiFileText,
    FiCalendar,
    FiShield,
    FiClock,
    FiCheckCircle,
    FiAlertCircle,
    FiSend,
} from 'react-icons/fi';
import { FaCar } from 'react-icons/fa';

const AgencyInfo = () => {
    const { user, updateUser } = useAuth();
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [agencyData, setAgencyData] = useState(null);
    const [pendingRequest, setPendingRequest] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        company: '',
        address: '',
        city: '',
        website: '',
        description: '',
    });
    const [stats, setStats] = useState({
        totalCars: 0,
        totalClients: 0,
        totalReservations: 0,
        activeReservations: 0,
    });

    useEffect(() => {
        fetchAgencyInfo();
        fetchStats();
        fetchPendingRequest();
    }, []);

    const fetchAgencyInfo = async () => {
        try {
            setLoading(true);
            const response = await api.get('/api/auth/me');
            const userData = response.data.data;
            console.log('DEBUG AgencyInfo userData:', userData); // Debug log
            setAgencyData(userData);
            setFormData({
                name: userData.name || '',
                email: userData.email || '',
                phone: userData.phone || userData.companyInfo?.telephone || '',
                company: userData.company || userData.companyInfo?.agence || '',
                address: userData.companyInfo?.adresse || userData.companyInfo?.address || '',
                city: userData.companyInfo?.ville || userData.companyInfo?.city || '',

                // Champs additionnels complets
                agence: userData.companyInfo?.agence || userData.company || '',
                bureau: userData.companyInfo?.bureau || '',

                website: userData.companyInfo?.website || '',
                description: userData.companyInfo?.description || '',
            });
        } catch (error) {
            console.error('Error fetching agency info:', error);
            toast.error('Erreur lors du chargement des informations');
        } finally {
            setLoading(false);
        }
    };

    const fetchStats = async () => {
        try {
            // Fetch car stats
            const carsRes = await api.get('/api/cars/stats/overview');
            // Fetch client stats
            const clientsRes = await api.get('/api/clients/stats/overview');
            // Fetch reservation stats
            const reservationsRes = await api.get('/api/reservations/stats/overview');

            setStats({
                totalCars: carsRes.data?.data?.totalCars || 0,
                totalClients: clientsRes.data?.data?.totalClients || 0,
                totalReservations: reservationsRes.data?.data?.totalReservations || 0,
                activeReservations: reservationsRes.data?.data?.activeReservations || 0,
            });
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    };

    const fetchPendingRequest = async () => {
        try {
            const response = await api.get('/api/agency-updates/my-request');
            console.log('DEBUG: fetchPendingRequest response:', response.data);
            setPendingRequest(response.data.data);
        } catch (error) {
            console.error('Error fetching pending request:', error);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSave = async () => {
        try {
            setSaving(true);

            // Create an update request instead of direct update
            // Use the same field names as MongoDB (adresse, ville, telephone, etc.)
            await api.post('/api/agency-updates', {
                name: formData.name,
                phone: formData.phone,
                company: formData.company,
                companyInfo: {
                    agence: formData.agence,
                    bureau: formData.bureau,
                    adresse: formData.address,
                    ville: formData.city,
                    telephone: formData.phone,
                    email: formData.email || '',
                }
            });

            toast.success('Demande de modification envoyée ! En attente de validation par le SuperAdmin.');
            setEditing(false);
            fetchPendingRequest();
        } catch (error) {
            console.error('Error creating update request:', error);
            toast.error(error.response?.data?.message || 'Erreur lors de l\'envoi de la demande');
        } finally {
            setSaving(false);
        }
    };

    const handleCancelRequest = async () => {
        try {
            await api.delete('/api/agency-updates/my-request');
            toast.success('Demande annulée');
            setPendingRequest(null);
        } catch (error) {
            console.error('Error canceling request:', error);
            toast.error('Erreur lors de l\'annulation de la demande');
        }
    };

    const handleCancel = () => {
        setFormData({
            name: agencyData?.name || '',
            email: agencyData?.email || '',
            phone: agencyData?.phone || agencyData?.companyInfo?.telephone || '',
            company: agencyData?.company || agencyData?.companyInfo?.agence || '',
            address: agencyData?.companyInfo?.adresse || '',
            city: agencyData?.companyInfo?.ville || '',

            // Restore additional fields
            agence: agencyData?.companyInfo?.agence || agencyData?.company || '',
            bureau: agencyData?.companyInfo?.bureau || '',
        });
        setEditing(false);
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center h-64 gap-4">
                <div className="relative">
                    <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-200 border-t-primary-600"></div>
                    <FiBriefcase className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-5 h-5 text-primary-600" />
                </div>
                <p className="text-gray-500 dark:text-gray-400 animate-pulse">Chargement des informations...</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-start gap-4">
                <div>
                    <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2 sm:gap-3">
                        <div className="p-1.5 sm:p-2 bg-gradient-to-br from-primary-500 to-primary-700 rounded-lg sm:rounded-xl shadow-lg">
                            <FiBriefcase className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
                        </div>
                        Informations de l'Agence
                    </h1>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                        Gérez les informations de votre agence de location
                    </p>
                </div>

                {!editing && (!pendingRequest || pendingRequest.status !== 'pending') ? (
                    <button
                        onClick={() => setEditing(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white font-medium rounded-xl transition-all text-sm w-fit"
                    >
                        <FiEdit2 className="w-4 h-4" />
                        <span className="hidden sm:inline">Modifier</span>
                    </button>
                ) : editing ? (
                    <div className="flex gap-2">
                        <button
                            onClick={handleCancel}
                            className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-medium rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-all text-sm"
                        >
                            <FiX className="w-4 h-4" />
                            <span className="hidden sm:inline">Annuler</span>
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={saving}
                            className="flex items-center gap-2 px-4 py-2 bg-brand-green hover:bg-green-700 text-white font-medium rounded-xl transition-all text-sm disabled:opacity-50"
                        >
                            <FiSend className="w-4 h-4" />
                            <span className="hidden sm:inline">{saving ? 'Envoi...' : 'Demander Modification'}</span>
                        </button>
                    </div>
                ) : null}
            </div>

            {/* Pending Request Banner */}
            {pendingRequest && pendingRequest.status === 'pending' && (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-xl p-4">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex items-center gap-3">
                            <div className="p-2 bg-yellow-100 dark:bg-yellow-900/40 rounded-lg">
                                <FiClock className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
                            </div>
                            <div>
                                <h3 className="font-medium text-yellow-800 dark:text-yellow-200">
                                    Demande de modification en attente
                                </h3>
                                <p className="text-sm text-yellow-600 dark:text-yellow-400">
                                    Votre demande est en cours d'examen par le SuperAdmin
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={handleCancelRequest}
                            className="flex items-center gap-2 px-4 py-2 bg-yellow-100 dark:bg-yellow-900/40 text-yellow-700 dark:text-yellow-300 font-medium rounded-lg hover:bg-yellow-200 dark:hover:bg-yellow-900/60 transition-all text-sm"
                        >
                            <FiX className="w-4 h-4" />
                            Annuler la demande
                        </button>
                    </div>
                </div>
            )}

            {/* Rejected Request Banner */}
            {pendingRequest && pendingRequest.status === 'rejected' && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 mb-4">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                        <div className="flex items-start gap-3">
                            <div className="p-2 bg-red-100 dark:bg-red-900/40 rounded-lg">
                                <FiAlertCircle className="w-5 h-5 text-red-600 dark:text-red-400" />
                            </div>
                            <div>
                                <h3 className="font-medium text-red-800 dark:text-red-200">
                                    Demande de modification rejetée
                                </h3>
                                <p className="text-sm text-red-600 dark:text-red-400 mt-1">
                                    Raison : {pendingRequest.rejectionReason || 'Aucune raison fournie'}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => setPendingRequest(null)}
                            className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 text-sm font-medium"
                        >
                            Fermer
                        </button>
                    </div>
                </div>
            )}

            {/* Stats Cards */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
                <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-xl shadow-sm border-t-4 border-primary-500 hover:shadow-lg transition-all duration-300">
                    <div className="flex items-center space-x-3">
                        <div className="p-2 sm:p-3 bg-primary-100 dark:bg-primary-900/40 rounded-full">
                            <FaCar className="h-5 w-5 sm:h-6 sm:w-6 text-primary-600 dark:text-primary-400" />
                        </div>
                        <div>
                            <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">Voitures</p>
                            <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">{stats.totalCars}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-xl shadow-sm border-t-4 border-brand-green hover:shadow-lg transition-all duration-300">
                    <div className="flex items-center space-x-3">
                        <div className="p-2 sm:p-3 bg-green-100 dark:bg-green-900/40 rounded-full">
                            <FiUser className="h-5 w-5 sm:h-6 sm:w-6 text-green-600 dark:text-green-400" />
                        </div>
                        <div>
                            <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">Clients</p>
                            <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">{stats.totalClients}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-xl shadow-sm border-t-4 border-purple-500 hover:shadow-lg transition-all duration-300">
                    <div className="flex items-center space-x-3">
                        <div className="p-2 sm:p-3 bg-purple-100 dark:bg-purple-900/40 rounded-full">
                            <FiCalendar className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600 dark:text-purple-400" />
                        </div>
                        <div>
                            <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">Réservations</p>
                            <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">{stats.totalReservations}</p>
                        </div>
                    </div>
                </div>

                <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-xl shadow-sm border-t-4 border-yellow-500 hover:shadow-lg transition-all duration-300">
                    <div className="flex items-center space-x-3">
                        <div className="p-2 sm:p-3 bg-yellow-100 dark:bg-yellow-900/40 rounded-full">
                            <FiClock className="h-5 w-5 sm:h-6 sm:w-6 text-yellow-600 dark:text-yellow-400" />
                        </div>
                        <div>
                            <p className="text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-400">Actives</p>
                            <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">{stats.activeReservations}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Agency Info Card */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
                {/* Card Header */}
                <div className="bg-gradient-to-r from-primary-500 to-primary-700 px-6 py-4">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-white/20 rounded-lg">
                            <FiBriefcase className="w-6 h-6 text-white" />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-white">
                                {formData.agence || formData.company || formData.name || 'Mon Agence'}
                            </h2>
                            <p className="text-sm text-white/70">
                                {user?.role === 'admin' ? 'Administrateur' : user?.role === 'staff' ? 'Staff' : 'Utilisateur'}
                            </p>
                        </div>
                        <div className="ml-auto">
                            {agencyData?.isActive !== false ? (
                                <span className="inline-flex items-center gap-1 px-3 py-1 bg-green-500/20 text-green-100 text-sm font-medium rounded-full">
                                    <FiCheckCircle className="w-4 h-4" />
                                    Actif
                                </span>
                            ) : (
                                <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-500/20 text-red-100 text-sm font-medium rounded-full">
                                    <FiAlertCircle className="w-4 h-4" />
                                    Inactif
                                </span>
                            )}
                        </div>
                    </div>
                </div>

                {/* Card Body */}
                <div className="p-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Left Column */}
                        <div className="space-y-4">
                            <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                Informations de Contact
                            </h3>

                            <div className="space-y-4">
                                <div className="flex items-start gap-3">
                                    <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                                        <FiUser className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                                    </div>
                                    <div className="flex-1">
                                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Nom</label>
                                        {editing ? (
                                            <input
                                                type="text"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                className="input w-full"
                                                placeholder="Votre nom"
                                            />
                                        ) : (
                                            <p className="text-gray-900 dark:text-white font-medium">{formData.name || '-'}</p>
                                        )}
                                    </div>
                                </div>

                                <div className="flex items-start gap-3">
                                    <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                                        <FiMail className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                                    </div>
                                    <div className="flex-1">
                                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Email</label>
                                        <p className="text-gray-900 dark:text-white font-medium">{formData.email || '-'}</p>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">L'email ne peut pas être modifié</p>
                                    </div>
                                </div>

                                <div className="flex items-start gap-3">
                                    <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                                        <FiPhone className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                                    </div>
                                    <div className="flex-1">
                                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Téléphone</label>
                                        {editing ? (
                                            <input
                                                type="tel"
                                                name="phone"
                                                value={formData.phone}
                                                onChange={handleChange}
                                                className="input w-full"
                                                placeholder="+212 6XX XXX XXX"
                                            />
                                        ) : (
                                            <p className="text-gray-900 dark:text-white font-medium">{formData.phone || '-'}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Right Column */}
                        <div className="space-y-4">
                            <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                                Informations de l'Entreprise
                            </h3>

                            <div className="space-y-4">
                                <div className="flex items-start gap-3">
                                    <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                                        <FiBriefcase className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                                    </div>
                                    <div className="flex-1">
                                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Nom (Company)</label>
                                        {editing ? (
                                            <input
                                                type="text"
                                                name="company"
                                                value={formData.company}
                                                onChange={handleChange}
                                                className="input w-full"
                                                placeholder="Nom de votre agence"
                                            />
                                        ) : (
                                            <p className="text-gray-900 dark:text-white font-medium">{formData.company || '-'}</p>
                                        )}
                                    </div>
                                </div>

                                {/* Agence Field */}
                                <div className="flex items-start gap-3">
                                    <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                                        <FiBriefcase className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                                    </div>
                                    <div className="flex-1">
                                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Agence</label>
                                        {editing ? (
                                            <input
                                                type="text"
                                                name="agence"
                                                value={formData.agence}
                                                onChange={handleChange}
                                                className="input w-full"
                                                placeholder="Nom de l'agence"
                                            />
                                        ) : (
                                            <p className="text-gray-900 dark:text-white font-medium">{formData.agence || '-'}</p>
                                        )}
                                    </div>
                                </div>

                                {/* Bureau Field */}
                                <div className="flex items-start gap-3">
                                    <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                                        <FiBriefcase className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                                    </div>
                                    <div className="flex-1">
                                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Bureau</label>
                                        {editing ? (
                                            <input
                                                type="text"
                                                name="bureau"
                                                value={formData.bureau}
                                                onChange={handleChange}
                                                className="input w-full"
                                                placeholder="Nom du bureau"
                                            />
                                        ) : (
                                            <p className="text-gray-900 dark:text-white font-medium">{formData.bureau || '-'}</p>
                                        )}
                                    </div>
                                </div>





                                <div className="flex items-start gap-3">
                                    <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                                        <FiMapPin className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                                    </div>
                                    <div className="flex-1">
                                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Adresse</label>
                                        {editing ? (
                                            <input
                                                type="text"
                                                name="address"
                                                value={formData.address}
                                                onChange={handleChange}
                                                className="input w-full"
                                                placeholder="Adresse de l'agence"
                                            />
                                        ) : (
                                            <p className="text-gray-900 dark:text-white font-medium">{formData.address || '-'}</p>
                                        )}
                                    </div>
                                </div>

                                <div className="flex items-start gap-3">
                                    <div className="p-2 bg-gray-100 dark:bg-gray-700 rounded-lg">
                                        <FiMapPin className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                                    </div>
                                    <div className="flex-1">
                                        <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">Ville</label>
                                        {editing ? (
                                            <input
                                                type="text"
                                                name="city"
                                                value={formData.city}
                                                onChange={handleChange}
                                                className="input w-full"
                                                placeholder="Ville"
                                            />
                                        ) : (
                                            <p className="text-gray-900 dark:text-white font-medium">{formData.city || '-'}</p>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>


                </div>

                {/* Card Footer */}
                <div className="bg-gray-50 dark:bg-gray-800/50 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex items-center justify-between text-sm text-gray-500 dark:text-gray-400">
                        <span>
                            <FiCalendar className="inline w-4 h-4 mr-1" />
                            Membre depuis: {agencyData?.createdAt ? new Date(agencyData.createdAt).toLocaleDateString('fr-FR') : '-'}
                        </span>
                        <span>
                            <FiShield className="inline w-4 h-4 mr-1" />
                            Rôle: <span className="capitalize font-medium">{user?.role}</span>
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AgencyInfo;
